import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import styled from 'styled-components'
import { Form, Input, Checkbox, Button, Modal, notification, Row, Col } from 'antd';
import { Icon } from 'dlweb-components';
import _, { set } from 'lodash';
import MembersTable from './MembersTable';
import UserSelectModal from './UserSelectModal';
import { useTableSelectionState }  from '../../../hooks'

const GroupDetails = ({selectedGroups, onSave}) => {

  const [form] = Form.useForm();
	const { TextArea } = Input;

  const keys = useMemo(() => ['id', 'inWorkflow'], []);

  const [dirty, setDirty] = useState(false);
  const [members, setMembers] = useState([]);
  const [initialValues, setInitialValues] = useState({});
  const [addUsersLoading, setAddUsersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pagination, setPagination] = useState({total: 0});
  const [sorterInfo, setSorterInfo] = useState({columnKey: 'ID', order: null});
  const [selectedUsers, setSelectedUsers] = useTableSelectionState(keys, []);
  const [singleUser, setSingleUser] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [currentGroup, setCurrentGroup] = useState('');

	const offset = {
    span: 24,
    offset: 0,
  };

  const readOnlyGroups = useMemo(() => ['Admins', 'Managers', 'Users'], []);
  
  useEffect(() => {
    setSingleUser(members?.length <= 1);
  }, [members, setSingleUser])
  
  const loadDetails = useCallback((options) => {

    setLoading(true);

    const gid = selectedGroups[0]?.id;
    setCurrentGroup(g => {
      if(g !== gid) {
        setSelectedUsers([]);
        setSorterInfo({columnKey: 'ID', order: 'ascend'});
        setPagination(p => ({ ...p, current: 1 }));
        return gid;
      }
      else {
        return g;
      }

      
    });

    setReadOnly(readOnlyGroups.includes(gid));

    const _options = {
      ...options,
      groupId: gid
    }
    
    if(!_options.skip) {
      _options.limit = 50;
      _options.skip = 0;
    }

    axios.post('/api/wt/groupman/dto/group/membership/read', _options).then(
      res => {
        setDirty(false);
        setMembers(res.data.data.memberships);
        setInitialValues(res.data.data.group);
        form.setFieldsValue(res.data.data.group);
        setPagination(p => ({ ...p, total: res.data._meta.totalRecords }));
        setLoading(false);
      }
    )
  }, [form, selectedGroups, setCurrentGroup, readOnlyGroups, setSelectedUsers]);

  useEffect(() => {
    if(!_.isEmpty(selectedGroups)) {
      if(currentGroup !== selectedGroups[0]?.id) {  
        loadDetails();
      }
    }
    else {
      setCurrentGroup('');
    }
  }, [selectedGroups, loadDetails, currentGroup]);

  const closeDialog = () => {
    setVisible(false)
  }

  const onOk = (newUsers) => {

    if(newUsers.length === 0) {
      closeDialog();
      return;
    }

    setAddUsersLoading(true);
    const isSingleUser = form.getFieldValue('singleUserRole');

    if(newUsers.length > 0) {

      const postData = {
        groupId: selectedGroups[0]?.id,
        addMembers: _.map(newUsers, 'id')
      }

      if(isSingleUser) {
        if(newUsers.length > 1) {
          Modal.error({
            title: 'Single user role',
            content: <div>This group is configured as a single user role.<br />Multiple users cannot be added to this group.</div>
          });
          setAddUsersLoading(false);
          return;
        }
        else {
          postData.removeMembers = [members[0]?.id];
        }
      }

      axios.post('/api/wt/groupman/dto/group/membership/update', postData).then(
        res => { 
          loadDetails();
          closeDialog();
          setSorterInfo({columnKey: 'ID', order: 'ascend'});
          setPagination(p => ({ ...p, current: 1 }));
          setAddUsersLoading(false);

          const msg = newUsers.length === 1 ? <span>User <strong>{newUsers[0].id}</strong> added to group.</span> : `${newUsers.length} users added to group.`;

          notification.success({ message: msg });
        }
      )
    }
  }

  const onDetailsSave = () => {

      setSaving(true);
      setLoading(true);
      setDirty(false);
      
      const values = form.getFieldsValue();

      if(values.id !== initialValues.id) {
        values.NewGroupId = values.id;
        values.id = initialValues.id;
      }

      axios.post('/api/wt/groupman/dto/group/update', values).then(res => { 
          const { errors } = res.data;

          loadDetails();

          if(errors.length > 0) {
            notification.error({ message: errors[1] || errors[0] });
            setSaving(false);
            return;
          }
          else {
            notification.success({ message: `Group updated successfully.` });
          }

          setSaving(false);
          onSave(values.NewGroupId || initialValues.id);
        }
      )
    
  }

  const onCancel = () => {
    form.setFieldsValue(initialValues);
    setDirty(false);
  }

  const onRemoveUser = () => {
    if(selectedUsers.length > 0) {
      setLoading(true);

      const postData = {
        groupId: selectedGroups[0]?.id,
        removeMembers: _.map(selectedUsers, 'id')
      }

      axios.post('/api/wt/groupman/dto/group/membership/update', postData).then(
        () => { 
          setSelectedUsers([]);
          setSorterInfo({columnKey: 'ID', order: 'ascend'});
          setPagination(p => ({ ...p, current: 1 }));

          const msg = selectedUsers.length === 1 ? <span>User <strong>{selectedUsers[0].id}</strong> removed from group.</span> : `${selectedUsers.length} users removed from group.`;

          notification.success({ message: msg });
          loadDetails();
        }
      )
    }
  }

  const handleTableChange = (pagination, filters, sorter) => {

    const pager = { ...pagination };
    pager.current = pagination.current;
    setPagination(pager);
        
    setSorterInfo(sorter);

    const cf = {
      limit: pagination.pageSize,
      skip: (pagination.current - 1) * pagination.pageSize,
      order: [{
        column: sorter.columnKey,
        direction: sorter.order === 'ascend' ? 0 : 1
      }]
    }

    loadDetails(cf);
  };

  const onFormChange = (changedValues, allValues) => {

    const { id } = allValues;

    if(id.trim() === '') {
      setDirty(false);
      return;
    }

    setDirty(!_.isEqual(allValues, initialValues));
  }

  return(
    <>
      <div className='modalRoot' />
      <Form
        form={form}
        name="groupForm"
        labelAlign="left"
        onValuesChange={onFormChange}
        autoComplete="off"
        requiredMark={false}
        disabled={loading}
      >
        <Form.Item
          label="Name"
          name="id"
          rules={[
            {
              required: true
            },
          ]}
          style={{ marginBottom: "8px" }}
          labelCol={offset}
          wrapperCol={offset}
        >
          <Input disabled={readOnly} maxLength={50} />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          style={{ marginBottom: "8px" }}
          labelCol={offset}
          wrapperCol={offset}
        >
          <TextArea
            rows={5}
            maxLength={500}
            style={{resize: 'none'}}
          />
        </Form.Item>
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item
              label=""
              name="singleUserRole"
              valuePropName="checked"
              style={{ marginBottom: "8px" }}
            >
              <Checkbox disabled={!singleUser}>Single user role</Checkbox>
            </Form.Item>
          </Col>
          <Col span={12}>
            <div style={{float: 'right', marginTop: '2px'}}>
            {dirty &&
              <Button
                onClick={onCancel}
                style={{marginRight: '10px'}}
              >
                Cancel
              </Button>
            }
            <Button
              onClick={onDetailsSave}
              type="primary"
              disabled={!dirty}
              loading={saving}
            >
              Save Changes
            </Button>
            </div>
          </Col>
        </Row>
      </Form>
      <ButtonBox>
        <Button
          type="primary"
          icon={<Icon type='user-plus' fixedWidth="25px" />}
          onClick={() => setVisible(true)}
          disabled={loading}
        >
          Add users to group
        </Button>
        {selectedUsers.length > 0 &&
          <Button
            type="primary"
            style={{marginLeft: '10px', background: 'var(--warning-color)', borderColor: 'var(--error-color)'}}
            icon={<Icon type='user-minus' fixedWidth="25px" />}
            onClick={() => onRemoveUser()}
            loading={loading}
          >
            Remove selected users
          </Button>
        }
      </ButtonBox>
      <MembersTable
        members={members}
        loading={loading}
        pagination={pagination}
        sorter={sorterInfo}
        onRemoveUser={onRemoveUser}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
        groupName={selectedGroups[0]?.id}
        handleTableChange={handleTableChange}
      />
      <UserSelectModal
        open={visible}
        onOk={onOk}
        loading={addUsersLoading}
        closeDialog={closeDialog}
        groupFilter={selectedGroups[0]?.id}
      />
      <DisableMembersOverlay dirty={dirty} />
    </>
	)
}

const DisableMembersOverlay = styled.div`
  position: absolute; 
  top: 300px; 
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.5);
  z-index: 100;
  display: ${p => p.dirty ? 'block' : 'none'};
`

const ButtonBox = styled.div`
  float: left;
  margin: 25px 0 5px 0;
`

export default GroupDetails;